import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Card } from '@atoms/Card';
import { authService } from '@services/authService';
import { geoService } from '@services/geoService';
import { useAuthStore } from '@store/authStore';
import { getErrorMessage } from '@services/api';
import { LocationMapModal } from '@molecules/ServiceAreaMap';

export function SettingsSection() {
  const { user, refreshUser } = useAuthStore();
  const [profile, setProfile] = useState({
    city: user?.city ?? '',
    district: user?.district ?? '',
    neighborhood: user?.neighborhood ?? '',
    address: user?.address ?? '',
    phone: user?.phone ?? '',
    latitude: user?.latitude,
    longitude: user?.longitude,
  });
  const [passwords, setPasswords] = useState({ current: '', next: '' });
  const [showMap, setShowMap] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const { data: neighborhoods } = useQuery({
    queryKey: ['neighborhoods', profile.city, profile.district],
    queryFn: () => geoService.getNeighborhoods(profile.city, profile.district),
    enabled: profile.city.length > 1 && profile.district.length > 1,
  });

  useEffect(() => {
    if (user) {
      setProfile({
        city: user.city ?? '',
        district: user.district ?? '',
        neighborhood: user.neighborhood ?? '',
        address: user.address ?? '',
        phone: user.phone ?? '',
        latitude: user.latitude,
        longitude: user.longitude,
      });
    }
  }, [user]);

  const profileMutation = useMutation({
    mutationFn: async () => {
      await authService.updateProfile({
        phone: profile.phone,
        address: profile.address,
      });
      await geoService.updateLocation({
        city: profile.city,
        district: profile.district,
        neighborhood: profile.neighborhood,
        address: profile.address,
        latitude: profile.latitude,
        longitude: profile.longitude,
      });
    },
    onSuccess: async () => {
      await refreshUser();
      setMessage('Teslimat adresi güncellendi');
    },
    onError: (err) => setMessage(getErrorMessage(err)),
  });

  const passwordMutation = useMutation({
    mutationFn: () => authService.changePassword(passwords.current, passwords.next),
    onSuccess: () => {
      setPasswords({ current: '', next: '' });
      setMessage('Şifre güncellendi');
    },
    onError: (err) => setMessage(getErrorMessage(err)),
  });

  const captureLocation = () => {
    if (!navigator.geolocation) {
      setMessage('Tarayıcı konum desteği yok');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setProfile({
          ...profile,
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
        setMessage('Konum alındı');
      },
      () => setMessage('Konum alınamadı'),
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Ayarlar</h2>
      {message && <p className="text-sm text-brand-600">{message}</p>}

      <Card className="space-y-3">
        <h3 className="font-medium">Teslimat Adresi</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <Input label="Telefon" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
          <Input label="İl" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value.toUpperCase(), neighborhood: '' })} />
          <Input label="İlçe" value={profile.district} onChange={(e) => setProfile({ ...profile, district: e.target.value.toUpperCase(), neighborhood: '' })} />
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Mahalle</span>
            <select
              value={profile.neighborhood}
              onChange={(e) => setProfile({ ...profile, neighborhood: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            >
              <option value="">Seçin</option>
              {(neighborhoods ?? []).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          <Input label="Adres" value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} className="md:col-span-2" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={captureLocation}>Konumumu Al</Button>
          <Button type="button" variant="outline" onClick={() => setShowMap(true)}>Haritada Seç</Button>
          {profile.latitude != null && profile.longitude != null && (
            <span className="self-center text-xs text-gray-500">
              {profile.latitude.toFixed(5)}, {profile.longitude.toFixed(5)}
            </span>
          )}
        </div>
        <Button loading={profileMutation.isPending} onClick={() => profileMutation.mutate()}>Kaydet</Button>
      </Card>

      <Card className="space-y-3">
        <h3 className="font-medium">Şifre Değiştir</h3>
        <Input label="Mevcut Şifre" type="password" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} />
        <Input label="Yeni Şifre" type="password" value={passwords.next} onChange={(e) => setPasswords({ ...passwords, next: e.target.value })} />
        <Button loading={passwordMutation.isPending} onClick={() => passwordMutation.mutate()}>Güncelle</Button>
      </Card>

      <LocationMapModal
        open={showMap}
        onClose={() => setShowMap(false)}
        latitude={profile.latitude}
        longitude={profile.longitude}
        onSelect={(lat, lng) => {
          setProfile({ ...profile, latitude: lat, longitude: lng });
          setMessage('Haritadan konum seçildi');
        }}
      />
    </div>
  );
}
